<template>
    <!--
        Form for the Special Order widget. Three real fields (name,
        description, images) plus a hidden honeypot. On submit:
          1. compress images client-side (canvas downscale + JPEG re-encode)
          2. POST a FormData multipart body to /widget/specialOrder
          3. on success: reset the form, surface a success message,
             optionally call a global hook the embedder configured
        Failure surfaces inline (no alert() — the host page may have
        its own UX patterns we shouldn't disrupt).
    -->
    <form @submit.prevent="onSubmit" novalidate>
        <div class="field">
            <label for="name" class="label label-required">Name</label>
            <input
                id="name"
                v-model.trim="form.name"
                type="text"
                required
                maxlength="200"
                autocomplete="name"
                class="input"
                :disabled="submitting"
            />
        </div>

        <div class="field">
            <label for="description" class="label label-required">Description</label>
            <textarea
                id="description"
                v-model.trim="form.description"
                rows="4"
                required
                maxlength="5000"
                class="textarea"
                :disabled="submitting"
            />
            <span class="help">
                {{ form.description.length }} / 5000
            </span>
        </div>

        <div class="field">
            <span class="label">Images</span>
            <ImageDropzone
                :images="form.images"
                :disabled="submitting"
                @add="onAddImages"
                @remove="onRemoveImage"
            />
            <span class="help">
                Up to {{ MAX_IMAGES }} images, {{ MAX_IMAGE_MB }}MB each.
                Photos will be compressed before upload.
            </span>
        </div>

        <!--
            Honeypot: bots fill every input, humans don't see this one.
            Backend silently 200's submissions where this is non-empty,
            so the bot thinks it succeeded and doesn't retry.
        -->
        <input
            v-model="form._company"
            type="text"
            name="_company"
            tabindex="-1"
            autocomplete="off"
            aria-hidden="true"
            class="honeypot"
        />

        <p v-if="error" class="alert alert-error" role="alert">{{ error }}</p>
        <p v-if="success" class="alert alert-success">
            Thanks — we received your request and will be in touch.
        </p>

        <div class="actions">
            <button type="submit" class="submit" :disabled="submitting || !canSubmit">
                {{ submitting ? 'Submitting…' : 'Submit Request' }}
            </button>
        </div>
    </form>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import ImageDropzone from './ImageDropzone.vue'
import { compressImage } from '@shared/compressImage.js'
import { joinUrl, postFormData } from '@shared/api.js'

const props = defineProps({
    config: { type: Object, required: true },
})

// Public limits kept here (rather than on the backend) so the UI can
// reject before round-tripping. Backend has its own matching limits;
// these two should be kept in sync.
const MAX_IMAGES = 10
const MAX_IMAGE_MB = 10
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024

// `reactive` rather than `ref({...})` so child components binding to
// nested fields (form.images) play nice with v-model + array mutations.
const form = reactive({
    name: '',
    description: '',
    images: [],   // File objects (compressed by the time they land here)
    _company: '', // honeypot
})

const submitting = ref(false)
const error = ref('')
const success = ref(false)

const canSubmit = computed(
    () => form.name.length > 0 && form.description.length > 0
)

async function onAddImages(files) {
    // Per-file: enforce count + size, compress, push. Errors on one
    // file shouldn't block the others.
    const remainingSlots = MAX_IMAGES - form.images.length
    if (remainingSlots <= 0) {
        error.value = `Max ${MAX_IMAGES} images.`
        return
    }
    error.value = ''
    const accepted = Array.from(files).slice(0, remainingSlots)
    for (const file of accepted) {
        if (!file.type || !file.type.startsWith('image/')) {
            error.value = `"${file.name}" is not an image and was skipped.`
            continue
        }
        if (file.size > MAX_IMAGE_BYTES) {
            error.value = `"${file.name}" is larger than ${MAX_IMAGE_MB}MB and was skipped.`
            continue
        }
        try {
            const compressed = await compressImage(file)
            form.images.push(compressed)
        } catch (e) {
            console.warn('[special-order] compression failed, using original:', e)
            form.images.push(file)
        }
    }
}

function onRemoveImage(index) {
    form.images.splice(index, 1)
}

async function onSubmit() {
    if (submitting.value || !canSubmit.value) return
    error.value = ''
    success.value = false
    submitting.value = true
    try {
        const url = joinUrl(props.config.apiBaseUrl, '/widget/specialOrder')
        const fd = new FormData()
        fd.append('name', form.name)
        fd.append('description', form.description)
        fd.append('_company', form._company)     // honeypot — see template
        for (const img of form.images) {
            fd.append('images', img, img.name)
        }
        const res = await postFormData(url, fd)
        if (!res || res.success === false) {
            throw new Error((res && res.message) || 'Submission failed')
        }
        success.value = true
        // Reset for the next submission. Don't clear in-flight props
        // (apiBaseUrl etc.) — only the user-entered fields.
        form.name = ''
        form.description = ''
        form.images = []
        form._company = ''
        // Fire the optional embedder callback. Lookup is guarded so a
        // typo'd name doesn't throw inside our flow.
        const cbName = props.config.onSuccessGlobal
        if (cbName && typeof window[cbName] === 'function') {
            try {
                window[cbName](res)
            } catch (cbErr) {
                console.warn(
                    `[special-order] onSuccess callback "${cbName}" threw:`,
                    cbErr
                )
            }
        }
    } catch (e) {
        error.value = e.message || 'Submission failed'
    } finally {
        submitting.value = false
    }
}
</script>
